import { useEffect, useRef, useState } from "react";
import type { ColorScheme, MainButtonParams, SecondaryButtonParams } from "./types";
import {
  colorScheme,
  onSchemeChange,
  setBackButton,
  setMainButton,
  setSecondaryButton,
} from "./telegram";

/**
 * Binds Telegram's native MainButton for as long as the calling screen is
 * mounted, then releases it. The click handler is held in a ref so a screen can
 * close over changing state without re-registering the button on every render —
 * re-registering mid-press is what produces the classic "fires twice" bug.
 */
export function useMainButton(params: MainButtonParams | null, onClick?: () => void): void {
  const handler = useRef(onClick);
  handler.current = onClick;

  // Params are compared by value: callers build the object inline, so a
  // reference check would rebind on every render.
  const key = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!key) return;
    return setMainButton({
      ...(JSON.parse(key) as MainButtonParams),
      onClick: () => handler.current?.(),
    });
  }, [key]);
}

export function useSecondaryButton(
  params: SecondaryButtonParams | null,
  onClick?: () => void,
): void {
  const handler = useRef(onClick);
  handler.current = onClick;

  const key = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!key) return;
    return setSecondaryButton({
      ...(JSON.parse(key) as SecondaryButtonParams),
      onClick: () => handler.current?.(),
    });
  }, [key]);
}

/** Shows Telegram's header BackButton while mounted. Pass null to leave it hidden. */
export function useBackButton(onBack: (() => void) | null): void {
  const handler = useRef(onBack);
  handler.current = onBack;

  const enabled = onBack !== null;

  useEffect(() => {
    if (!enabled) return;
    return setBackButton(() => handler.current?.());
  }, [enabled]);
}

export function useColorScheme(): ColorScheme {
  const [scheme, setScheme] = useState<ColorScheme>(colorScheme);
  useEffect(() => onSchemeChange(setScheme), []);
  return scheme;
}
